import type { ReactNode } from 'react'

import { isFunction } from 'lodash'
import { config, useTransition } from 'react-spring'

import { type IModalProps } from '../Modal'
import { DrawerContent } from './components/DrawerContent'
import { DrawerContainerHeaderStyled } from './styles/DrawerContainerHeaderStyled'
import { DrawerContainerStyled } from './styles/DrawerContainerStyled'
import { DrawerModalStyled } from './styles/DrawerModalStyled'
import { TransparentIconButton } from '@ui/elements/Button'
import { IconX } from '@ui/elements/Icon'
import { Toolbar } from '@ui/elements/Toolbar'

interface DrawerRenderProps {}

type DrawerRenderFunction = (DrawerRenderProps: DrawerRenderProps) => ReactNode

export interface IDrawerProps extends Omit<IModalProps, 'open'> {
  drawerOpen: boolean
  setDrawerOpen: (open: IDrawerProps['drawerOpen']) => void
  minWidth?: string
  DrawerProps?: {
    renderTitle?: DrawerRenderFunction
    renderFooter?: DrawerRenderFunction
  }
  DocumentProps?: {
    renderDocument?: DrawerRenderFunction
    closeDrawerOnEsc?: boolean
  }
  children?: DrawerRenderFunction
  documentOpen?: boolean
  setDocumentOpen?: (open: IDrawerProps['documentOpen']) => void
}

export interface IDrawer {
  (props: IDrawerProps): JSX.Element
  Content: typeof DrawerContent
  displayName?: string
}

export const Drawer = ({
  DrawerProps = {},
  DocumentProps = {},
  drawerOpen,
  setDrawerOpen,
  minWidth,
  documentOpen,
  setDocumentOpen = () => undefined,
  children,
  ...ModalProps
}: IDrawerProps) => {
  const { renderFooter, renderTitle } = DrawerProps
  const { renderDocument } = DocumentProps

  const renderProps = {}

  const closeDrawer = () => {
    if (documentOpen && !DocumentProps.closeDrawerOnEsc) {
      setDocumentOpen(false)
    } else {
      setDrawerOpen(false)
    }
  }
  // Animate document
  const transitions = useTransition(documentOpen, null, {
    config: config.gentle,
    from: { opacity: 0, transform: 'translate3d(0, 100%, 0)' },
    enter: { opacity: 1, transform: 'translate3d(0, 0, 0)' },
    leave: { opacity: 0, transform: 'translate3d(0, -100%, 0)' },
  })
  return (
    <DrawerModalStyled
      scroll='body'
      open={drawerOpen}
      maxWidth='sm'
      onClose={(_, reason) => {
        if (reason === 'escapeKeyDown' || reason === 'backdropClick') {
          closeDrawer()
        }
      }}
      disableEscapeKeyDown={false}
      fullScreen
      {...ModalProps}
    >
      {drawerOpen &&
        isFunction(renderDocument) &&
        transitions.map(
          ({ item, key, props }) =>
            item && (
              <div
                key={key}
                style={{ ...props, height: '100%' }}
                data-testid='document-container'
              >
                {renderDocument(renderProps)}
              </div>
            )
        )}
      <DrawerContainerStyled minWidth={minWidth}>
        <Toolbar p={1}>
          <DrawerContainerHeaderStyled>
            {isFunction(renderTitle) && renderTitle(renderProps)}
          </DrawerContainerHeaderStyled>
          <Toolbar.Item ml='auto'>
            <TransparentIconButton
              data-testid='drawer-close-btn'
              iconComponent={<IconX />}
              onClick={() => {
                setDrawerOpen(false)
              }}
              aria-label='Close drawer'
            />
          </Toolbar.Item>
        </Toolbar>
        {isFunction(children) && children(renderProps)}
        {isFunction(renderFooter) && renderFooter(renderProps)}
      </DrawerContainerStyled>
    </DrawerModalStyled>
  )
}

Drawer.Content = DrawerContent

Drawer.displayName = 'Drawer'
